import React from 'react';

import { ClinicianDatePicker } from '../../../atoms/date-picker/clinician-date-picker';
import { Input } from '../../../atoms/input/input';
import { Label } from '../../../atoms/label/label';
import { MultiSelect, MultiSelectOption } from '../../../atoms/multi-select/multi-select';
import { Select, SelectOption } from '../../../atoms/select/select';

const MAX_HOURS = 23;
const MAX_MINS_SECONDS = 59;
const MAX_MILLIS = 999;

function useDateFilterCallback(
  id: string,
  onDateChange: (id2: string, key: 'from' | 'to', value: Date | null) => void
) {
  return React.useCallback(
    (key: 'from' | 'to') => (value: Date | undefined) => {
      if (value) {
        if (key === 'from') {
          value.setHours(0, 0, 0, 0);
        }
        if (key === 'to') {
          value.setHours(MAX_HOURS, MAX_MINS_SECONDS, MAX_MINS_SECONDS, MAX_MILLIS);
        }
      }
      onDateChange(id, key, value ?? null);
    },
    [id, onDateChange]
  );
}

interface IDateFilterProps {
  id: string;
  name: string;
  fromDate: Date | null | undefined;
  toDate: Date | null | undefined;
  onDateChange: (id: string, key: 'from' | 'to', value: Date | null) => void;
}

const DateFilterComponent: React.FC<IDateFilterProps> = ({ id, name, fromDate, toDate, onDateChange }) => {
  const handleDateChange = useDateFilterCallback(id, onDateChange);
  const currentYear = new Date().getFullYear();

  return (
    <div className="flex flex-col gap-2">
      <Label>{name}</Label>
      <div className="flex flex-col items-start gap-2">
        <Label htmlFor={`${id}-from`} id={`${id}-from-label`}>
          From
        </Label>
        <ClinicianDatePicker
          ariaLabelledby={`${id}-from-label`}
          captionLayout="dropdown-buttons"
          date={fromDate ?? undefined}
          fromYear={1900}
          id={`${id}-from`}
          maxDate={toDate ?? undefined}
          setDate={handleDateChange('from')}
          toYear={currentYear}
        />
        <Label htmlFor={`${id}-to`} id={`${id}-to-label`}>
          To
        </Label>
        <ClinicianDatePicker
          ariaLabelledby={`${id}-to-label`}
          captionLayout="dropdown-buttons"
          date={toDate ?? undefined}
          fromYear={1900}
          id={`${id}-to`}
          minDate={fromDate ?? undefined}
          setDate={handleDateChange('to')}
          toYear={currentYear}
        />
      </div>
    </div>
  );
};

function useTextFilterCallback(id: string, onTextChange: (id2: string, value: string | null) => void) {
  return React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value: inputValue } = e.target;
      onTextChange(id, inputValue);
    },
    [id, onTextChange]
  );
}

interface ITextFilterProps {
  id: string;
  name: string;
  value: string | null;
  onTextChange: (id: string, value: string | null) => void;
}

const TextFilterComponent: React.FC<ITextFilterProps> = ({ id, name, value, onTextChange }) => {
  const handleTextChange = useTextFilterCallback(id, onTextChange);

  return (
    <div className="flex flex-col align-left gap-2">
      <Label htmlFor={id} id={id + '-label'}>
        {name}
      </Label>
      <Input
        aria-labelledby={id + '-label'}
        className="w-full"
        id={id}
        onChange={handleTextChange}
        size="small"
        type="text"
        value={value ?? ''}
      />
    </div>
  );
};

function useSelectFilterCallback(id: string, onValueChange: (id2: string, value: string | null) => void) {
  return React.useCallback(
    (value: string) => {
      onValueChange(id, value);
    },
    [id, onValueChange]
  );
}

interface ISelectFilterProps {
  id: string;
  name: string;
  values: string[];
  defaultValue: string | null;
  selectedValue: string | null;
  onValueChange: (id: string, value: string | null) => void;
}

const SelectFilterComponent: React.FC<ISelectFilterProps> = ({
  id,
  name,
  values,
  defaultValue,
  selectedValue,
  onValueChange,
}) => {
  const handleValueChange = useSelectFilterCallback(id, onValueChange);

  return (
    <div className="flex flex-col align-left gap-2">
      <Label htmlFor={id} id={id + '-label'}>
        {name}
      </Label>
      <Select
        aria-labelledby={`${id}-select`}
        defaultValue={defaultValue ?? undefined}
        id={id}
        onValueChange={handleValueChange}
        value={selectedValue ?? ''}
      >
        {values.map((val) => (
          <SelectOption key={val} value={val}>
            {val}
          </SelectOption>
        ))}
      </Select>
    </div>
  );
};

function useMultiSelectFilterCallback(id: string, onValueChange: (id2: string, value: string[] | null) => void) {
  return React.useCallback(
    (value: string[]) => {
      onValueChange(id, value);
    },
    [id, onValueChange]
  );
}

interface IMultiSelectFilterProps {
  id: string;
  name: string;
  values: string[];
  defaultValue: string[] | null;
  selectedValues: string[] | null;
  onValueChange: (id: string, value: string[] | null) => void;
}

const MultiSelectFilterComponent: React.FC<IMultiSelectFilterProps> = ({
  id,
  name,
  values,
  defaultValue,
  selectedValues,
  onValueChange,
}) => {
  const handleValueChange = useMultiSelectFilterCallback(id, onValueChange);

  return (
    <div className="flex flex-col align-left gap-2">
      <Label htmlFor={id} id={id + '-label'}>
        {name}
      </Label>
      <MultiSelect
        aria-labelledby={`${id}-select`}
        defaultValue={defaultValue ?? undefined}
        id={id}
        onValueChange={handleValueChange}
        value={selectedValues ?? undefined}
      >
        {values.map((val) => (
          <MultiSelectOption key={val} value={val}>
            {val}
          </MultiSelectOption>
        ))}
      </MultiSelect>
    </div>
  );
};

export {
  DateFilterComponent,
  MultiSelectFilterComponent,
  SelectFilterComponent,
  TextFilterComponent,
  useDateFilterCallback,
  useMultiSelectFilterCallback,
  useSelectFilterCallback,
  useTextFilterCallback,
};
